import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar,
} from "recharts";
import {
  Loader2, Trophy, MousePointerClick, UserPlus, TrendingUp, BarChart3,
  Plus, Trash2, Eye, EyeOff, Pencil, Save, X,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── CTA Variant Manager ──
function CTAVariantManager({ darkMode: dm }) {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("gmp_token")}` };

  const fetchVariants = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/cta-variants`, { headers });
      setVariants(res.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVariants(); }, [fetchVariants]);

  const startNew = () => setForm({ name: "", button_text: "", button_color: "#e0482d", is_active: true, _isNew: true });
  const startEdit = (v) => setForm({ ...v, _isNew: false });
  const cancel = () => setForm(null);

  const save = async () => {
    if (!form.button_text.trim()) { toast.error("Button text is required"); return; }
    setSaving(true);
    try {
      if (form._isNew) {
        await axios.post(`${API}/admin/cta-variants`, form, { headers });
        toast.success("CTA variant created");
      } else {
        await axios.put(`${API}/admin/cta-variants/${form.id}`, form, { headers });
        toast.success("Updated");
      }
      cancel(); fetchVariants();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this CTA variant?")) return;
    try {
      await axios.delete(`${API}/admin/cta-variants/${id}`, { headers });
      fetchVariants(); toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const toggleActive = async (v) => {
    try {
      await axios.put(`${API}/admin/cta-variants/${v.id}`, { is_active: !v.is_active }, { headers });
      fetchVariants();
    } catch { toast.error("Failed"); }
  };

  if (loading) return null;

  return (
    <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : ""}`} data-testid="cta-variant-manager">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>A/B Test CTA Variants</CardTitle>
          <Button size="sm" onClick={startNew} className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-lg text-xs h-7"
            data-testid="add-cta-variant-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Variant
          </Button>
        </div>
        <p className={`text-xs ${dm ? "text-gray-500" : "text-gray-400"}`}>
          Create different sign-up button variants. Active variants are randomly shown to quiz takers.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {form && (
          <div className={`p-3 rounded-xl border space-y-2 ${dm ? "bg-gray-900/50 border-gray-700" : "bg-gray-50 border-gray-200"}`}
            data-testid="cta-variant-form">
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Variant name (e.g. Urgency)"
                className={`rounded-lg h-8 text-xs ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                data-testid="cta-variant-name" />
              <Input value={form.button_text} onChange={e => setForm(f => ({ ...f, button_text: e.target.value }))}
                placeholder="Button text (e.g. Start Your BECE Prep)"
                className={`rounded-lg h-8 text-xs ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                data-testid="cta-variant-text" />
            </div>
            <div className="flex items-center gap-2">
              <label className={`text-[10px] ${dm ? "text-gray-400" : "text-gray-500"}`}>Color:</label>
              <input type="color" value={form.button_color} onChange={e => setForm(f => ({ ...f, button_color: e.target.value }))}
                className="w-8 h-6 rounded cursor-pointer border-0" data-testid="cta-variant-color" />
              <div className="flex-1" />
              <Button size="sm" variant="ghost" onClick={cancel} className={`h-7 text-xs ${dm ? "text-gray-400" : ""}`}>
                <X className="w-3 h-3 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={saving}
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="save-cta-variant-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
              </Button>
            </div>
            {/* Preview */}
            {form.button_text && (
              <div className="flex items-center gap-2 pt-1">
                <span className={`text-[10px] ${dm ? "text-gray-500" : "text-gray-400"}`}>Preview:</span>
                <button className="px-4 py-1.5 rounded-xl text-xs font-semibold text-white"
                  style={{ backgroundColor: form.button_color }}>
                  {form.button_text}
                </button>
              </div>
            )}
          </div>
        )}

        {variants.length === 0 && !form ? (
          <p className={`text-xs text-center py-4 ${dm ? "text-gray-500" : "text-gray-400"}`}>
            No CTA variants. The default "Create Free Account" button is used. Add variants to start A/B testing.
          </p>
        ) : (
          variants.map(v => (
            <div key={v.id}
              className={`flex items-center gap-3 px-3 py-2 rounded-xl ${dm ? "bg-gray-700/50" : "bg-gray-50"}`}
              data-testid={`cta-variant-${v.id}`}>
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: v.button_color }} />
              <div className="flex-1 min-w-0">
                <span className={`text-xs font-medium ${dm ? "text-gray-200" : "text-gray-700"}`}>{v.name || "Unnamed"}</span>
                <span className={`text-[10px] ml-2 ${dm ? "text-gray-500" : "text-gray-400"}`}>"{v.button_text}"</span>
              </div>
              <Badge className={`text-[10px] ${v.is_active ? "bg-emerald-500/15 text-emerald-500" : "bg-gray-500/15 text-gray-500"}`}>
                {v.is_active ? "Active" : "Off"}
              </Badge>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => toggleActive(v)} className={`h-6 w-6 p-0 ${dm ? "text-gray-400" : ""}`}>
                  {v.is_active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => startEdit(v)} className={`h-6 w-6 p-0 ${dm ? "text-gray-400" : ""}`}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(v.id)} className="h-6 w-6 p-0 text-red-500">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Analytics Component ──
export function QuizAnalytics({ darkMode: dm }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("gmp_token");
        const res = await axios.get(`${API}/admin/quiz-analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  if (!data) {
    return (
      <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : ""}`}>
        <CardContent className="py-12 text-center">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30 text-gray-400" />
          <p className={`text-sm font-medium ${dm ? "text-gray-300" : "text-gray-600"}`}>No analytics data yet</p>
          <p className={`text-xs mt-1 ${dm ? "text-gray-500" : "text-gray-400"}`}>Data will appear once visitors start taking sample quizzes</p>
        </CardContent>
      </Card>
    );
  }

  const { summary, chart_data, mock_breakdown, cta_breakdown } = data;

  const statCards = [
    { label: "Quiz Completions", value: summary.quiz_completions, icon: Trophy, color: "text-blue-500", bg: "bg-blue-500/10" },
    { label: "Sign-Up Clicks", value: summary.signup_clicks, icon: MousePointerClick, color: "text-amber-500", bg: "bg-amber-500/10" },
    { label: "Registrations", value: summary.registrations, icon: UserPlus, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    { label: "Conversion Rate", value: `${summary.conversion_rate}%`, icon: TrendingUp, color: "text-purple-500", bg: "bg-purple-500/10" },
  ];

  return (
    <div className="space-y-5" data-testid="quiz-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label} className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : ""}`}
            data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <div>
                  <p className={`text-xl font-bold ${dm ? "text-white" : "text-gray-900"}`}>{value}</p>
                  <p className={`text-[11px] ${dm ? "text-gray-400" : "text-gray-500"}`}>{label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Line Chart */}
      <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : ""}`} data-testid="conversion-chart">
        <CardHeader className="pb-0">
          <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>Quiz Conversion Funnel Over Time</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {chart_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chart_data} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="0" stroke={dm ? "#374151" : "#e5e7eb"} vertical={false} />
                <XAxis dataKey="date" tickFormatter={d => { const [,m,day] = d.split("-"); return `${day}/${m}`; }}
                  tick={{ fill: dm ? "#9ca3af" : "#6b7280", fontSize: 11 }} axisLine={{ stroke: dm ? "#4b5563" : "#d1d5db" }} tickLine={false} />
                <YAxis tick={{ fill: dm ? "#9ca3af" : "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} width={35} allowDecimals={false} />
                <Tooltip contentStyle={{
                  backgroundColor: dm ? "#1f2937" : "#fff", border: `1px solid ${dm ? "#374151" : "#e5e7eb"}`,
                  borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: 12,
                }} labelStyle={{ color: dm ? "#d1d5db" : "#374151", fontWeight: 600, marginBottom: 4 }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Line type="monotone" dataKey="completions" name="Completions" stroke="#5b9bd5" strokeWidth={3}
                  dot={{ r: 5, fill: "#5b9bd5", stroke: "#fff", strokeWidth: 2 }}
                  activeDot={{ r: 7, stroke: "#5b9bd5", strokeWidth: 2, fill: "#fff" }}
                  connectNulls />
                <Line type="monotone" dataKey="signups" name="Sign-Up Clicks" stroke="#f59e0b" strokeWidth={3}
                  dot={{ r: 5, fill: "#f59e0b", stroke: "#fff", strokeWidth: 2 }}
                  activeDot={{ r: 7, stroke: "#f59e0b", strokeWidth: 2, fill: "#fff" }}
                  connectNulls />
                <Line type="monotone" dataKey="registrations" name="Registrations" stroke="#10b981" strokeWidth={3}
                  dot={{ r: 5, fill: "#10b981", stroke: "#fff", strokeWidth: 2 }}
                  activeDot={{ r: 7, stroke: "#10b981", strokeWidth: 2, fill: "#fff" }}
                  connectNulls />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className={`text-sm text-center py-8 ${dm ? "text-gray-500" : "text-gray-400"}`}>
              No chart data yet. Data appears once visitors complete quizzes.
            </p>
          )}
        </CardContent>
      </Card>

      {/* CTA A/B Test Results */}
      {cta_breakdown && cta_breakdown.length > 0 && (
        <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : ""}`} data-testid="cta-performance">
          <CardHeader className="pb-2">
            <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>CTA Variant Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(120, cta_breakdown.length * 45)}>
              <BarChart data={cta_breakdown} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 10 }}>
                <CartesianGrid strokeDasharray="0" stroke={dm ? "#374151" : "#e5e7eb"} horizontal={false} />
                <XAxis type="number" tick={{ fill: dm ? "#9ca3af" : "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="variant_name" tick={{ fill: dm ? "#d1d5db" : "#374151", fontSize: 11 }} axisLine={false} tickLine={false} width={100} />
                <Tooltip contentStyle={{
                  backgroundColor: dm ? "#1f2937" : "#fff", border: `1px solid ${dm ? "#374151" : "#e5e7eb"}`,
                  borderRadius: 12, fontSize: 12, color: dm ? "#f3f4f6" : "#111827",
                }} labelStyle={{ color: dm ? "#f3f4f6" : "#374151", fontWeight: 600 }}
                   itemStyle={{ color: dm ? "#e5e7eb" : "#111827" }} />
                <Bar dataKey="clicks" name="Sign-Up Clicks" fill="#e0482d" radius={[0, 6, 6, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Per-Mock Breakdown */}
        {mock_breakdown.length > 0 && (
          <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : ""}`} data-testid="mock-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>Performance by Sample Mock</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {mock_breakdown.map((m, i) => (
                  <div key={m.mock_id}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-xl ${dm ? "bg-gray-700/50" : "bg-gray-50"}`}
                    data-testid={`mock-stat-${i}`}>
                    <span className={`text-xs font-mono ${dm ? "text-gray-400" : "text-gray-500"}`}>{m.mock_id.slice(0, 8)}</span>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`text-sm font-bold ${dm ? "text-white" : "text-gray-900"}`}>{m.completions}</p>
                        <p className={`text-[10px] ${dm ? "text-gray-500" : "text-gray-400"}`}>attempts</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-bold ${m.avg_score >= 70 ? "text-emerald-500" : m.avg_score >= 50 ? "text-amber-500" : "text-red-500"}`}>
                          {m.avg_score}%
                        </p>
                        <p className={`text-[10px] ${dm ? "text-gray-500" : "text-gray-400"}`}>avg score</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA Variant Manager */}
        <CTAVariantManager darkMode={dm} />
      </div>
    </div>
  );
}
